import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Form } from 'react-bootstrap';
import Chatbot from './Chatbot';

const PriceQuote = () => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [packageAnnouncement, setPackageAnnouncement] = useState(null);
  const [customizations, setCustomizations] = useState({
    guests: 50,
    venue: 'beachfront',
    catering: 'standard',
    decoration: 'standard'
  });
  const [totalEstimate, setTotalEstimate] = useState(0);

  const packages = {
    luxury: {
      name: 'Luxury Package',
      price: 25000,
      vendors: [
        'Oceanview Villa (Venue)',
        "Sari's Catering (Catering)",
        'Golden Petal Decor (Decoration)',
        'Sunset Studio (Photography)',
        'Island Beats (Entertainment)'
      ],
      features: [
        'Premium beachfront venue',
        'Gourmet catering for up to 200 guests',
        'Luxury decoration and flowers',
        'Professional photography and videography',
        'Live band and DJ',
        'Premium bar service'
      ]
    },
    standard: {
      name: 'Standard Package',
      price: 15000,
      vendors: [
        'Garden Estate (Venue)',
        "Bali Buffet Co. (Catering)",
        'Bloom & Co. (Decoration)',
        'FrameStory (Photography)',
        'DJ Kuta (Entertainment)'
      ],
      features: [
        'Beach or garden venue',
        'Buffet catering for up to 100 guests',
        'Standard decoration package',
        'Photography and videography',
        'DJ service',
        'Basic bar service'
      ]
    },
    economy: {
      name: 'Economy Package',
      price: 8000,
      vendors: [
        'Green Lawn (Venue)',
        'Local Eats (Catering)',
        'Simple Blooms (Decoration)',
        'SnapShot (Photography)',
        'Basic Sound (Entertainment)'
      ],
      features: [
        'Garden venue',
        'Basic catering for up to 50 guests',
        'Simple decoration',
        'Photography package',
        'Sound system rental',
        'Non-alcoholic beverages'
      ]
    }
  };

  const calculateEstimate = () => {
    let basePrice = selectedPackage ? packages[selectedPackage].price : 0;
    let extras = 0;
    
    // Add customization costs
    extras += (customizations.guests - 50) * 100; // $100 per additional guest
    if (customizations.venue === 'beachfront') extras += 2000;
    if (customizations.catering === 'premium') extras += 3000;
    if (customizations.decoration === 'premium') extras += 2500;

    setTotalEstimate(basePrice + extras);
  };

  return (
    <Container className="my-5">
      <h1 className="text-center mb-5">Wedding Packages</h1>
      
      {/* Package Selection */}
      <Row className="mb-5">
        {Object.entries(packages).map(([key, package_]) => (
          <Col key={key} md={4}>
            <Card className={`h-100 ${selectedPackage === key ? 'border-primary' : ''}`}>
              <Card.Body>
                <Card.Title>{package_.name}</Card.Title>
                <Card.Text className="h2 mb-4">${package_.price}</Card.Text>
                <ul>
                  {package_.features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
                <Button 
                  variant={selectedPackage === key ? 'primary' : 'outline-primary'}
                  onClick={() => {
                    setSelectedPackage(key);
                    // pass package info to Chatbot for typewriter announcement
                    setPackageAnnouncement({
                      key,
                      name: package_.name,
                      vendors: package_.vendors || []
                    });
                  }}
                  className="w-100"
                >
                  Select Package
                </Button>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Customization Options */}
      {selectedPackage && (
        <div className="mt-4">
          <h2>Customize Your Package</h2>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Number of Guests</Form.Label>
                  <Form.Control 
                    type="number" 
                    value={customizations.guests}
                    onChange={(e) => setCustomizations({...customizations, guests: parseInt(e.target.value)})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Venue Type</Form.Label>
                  <Form.Select 
                    value={customizations.venue}
                    onChange={(e) => setCustomizations({...customizations, venue: e.target.value})}
                  >
                    <option value="garden">Garden</option>
                    <option value="beachfront">Beachfront</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Catering Level</Form.Label>
                  <Form.Select 
                    value={customizations.catering}
                    onChange={(e) => setCustomizations({...customizations, catering: e.target.value})}
                  >
                    <option value="standard">Standard</option>
                    <option value="premium">Premium</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Decoration Level</Form.Label>
                  <Form.Select 
                    value={customizations.decoration}
                    onChange={(e) => setCustomizations({...customizations, decoration: e.target.value})}
                  >
                    <option value="standard">Standard</option>
                    <option value="premium">Premium</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Button variant="primary" onClick={calculateEstimate}>
              Calculate Estimate
            </Button>
          </Form>

        {totalEstimate > 0 && (
        <div className="mt-4">
            <Card>
            <Card.Header>
                <h3>Cost Breakdown</h3>
            </Card.Header>
            <Card.Body>
                <Row>
                <Col>
                    <p>Base Package ({packages[selectedPackage].name})</p>
                    <p>Additional Guests ({Math.max(0, customizations.guests - 50)} guests)</p>
                    <p>Venue Upgrade (Beachfront)</p>
                    <p>Catering Upgrade</p>
                    <p>Decoration Upgrade</p>
                    <p className="fw-bold">Total Estimate</p>
                </Col>
                <Col className="text-end">
                    <p>${packages[selectedPackage].price.toLocaleString()}</p>
                    <p>${(Math.max(0, customizations.guests - 50) * 100).toLocaleString()}</p>
                    <p>${(customizations.venue === 'beachfront' ? 2000 : 0).toLocaleString()}</p>
                    <p>${(customizations.catering === 'premium' ? 3000 : 0).toLocaleString()}</p>
                    <p>${(customizations.decoration === 'premium' ? 2500 : 0).toLocaleString()}</p>
                    <p className="fw-bold">${totalEstimate.toLocaleString()}</p>
                </Col>
                </Row>
                <p className="text-muted mt-3 mb-0">
                This is an estimate. Final pricing may vary based on specific requirements and availability.
                </p>
            </Card.Body>
            </Card>
            <Chatbot
              packageAnnouncement={packageAnnouncement}
              onPackageAnnounced={() => setPackageAnnouncement(null)}
            />
        </div>
        )}
        </div>
      )}
    </Container>
  );
};

export default PriceQuote;